import React, { useState, useMemo, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { formatDate } from "@/lib/utils";
import {
  PlusCircle,
  Eye,
  CheckCircle,
  XCircle,
  MoreVertical,
  Clock,
  ShieldAlert,
  AlertTriangle,
  PackageCheck,
  Truck,
  RotateCcw,
  CheckCircle2,
  Trash2,
  Filter,
} from "lucide-react";
import { DataTable } from "@/components/ui/data-table";
import { Button } from "@/components/ui/button";
import {
  PageHeader,
  PageHeaderHeading,
  PageHeaderActions,
} from "@/components/ui/page-header";
import { FilterCard, FilterSearch } from "@/components/ui/filter-card";
import { TableCard } from "@/components/ui/table-card";
import { Badge } from "@/components/ui/badge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { PageContainer } from "@/components/layout/PageContainer";
import warrantyApi from "@/api/warrantyApi";
import { all_routes } from "@/Router/all_routes";
import {
  getClaimStatusValue,
  getClaimStatusLabel,
  canTransition,
} from "@/lib/warrantyUtils";

import AssessClaimModal from "./components/AssessClaimModal";
import ApproveClaimModal from "./components/ApproveClaimModal";
import FulfilReplacementModal from "./components/FulfilReplacementModal";
import RejectClaimModal from "./components/RejectClaimModal";
import ResolveClaimModal from "./components/ResolveClaimModal";
import SupplierRmaModal from "./components/SupplierRmaModal";

const statusVariants = {
  pending: "warning",
  under_assessment: "secondary",
  approved: "info",
  in_progress: "secondary",
  resolved: "success",
  closed: "outline",
  rejected: "destructive",
};

const WarrantyClaimsList = () => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const location = useLocation();

  // Filters state per OpenAPI specification
  const [searchText, setSearchText] = useState("");
  const [debouncedSearchText, setDebouncedSearchText] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [replacementStatusFilter, setReplacementStatusFilter] = useState("all");
  const [returnConditionFilter, setReturnConditionFilter] = useState("all");
  const [rejectionReasonFilter, setRejectionReasonFilter] = useState("all");
  const [overdueFilter, setOverdueFilter] = useState("all");
  const [sortFilter, setSortFilter] = useState("created_at:desc");

  // Debounce search input
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearchText(searchText.trim());
    }, 350);
    return () => clearTimeout(handler);
  }, [searchText]);

  // Selected claim for modals
  const [activeClaim, setActiveClaim] = useState(null);

  // Modals state
  const [assessModalOpen, setAssessModalOpen] = useState(false);
  const [approveModalOpen, setApproveModalOpen] = useState(false);
  const [fulfilModalOpen, setFulfilModalOpen] = useState(false);
  const [rejectModalOpen, setRejectModalOpen] = useState(false);
  const [resolveModalOpen, setResolveModalOpen] = useState(false);
  const [supplierModalOpen, setSupplierModalOpen] = useState(false);

  // Confirm dialogs
  const [closeConfirmOpen, setCloseConfirmOpen] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);

  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 15,
  });

  const handleFilterChange = (setter) => (eOrVal) => {
    const val =
      eOrVal && typeof eOrVal === "object" && "target" in eOrVal
        ? eOrVal.target.value
        : eOrVal;
    setter(val);
    setPagination((prev) => (prev.pageIndex === 0 ? prev : { ...prev, pageIndex: 0 }));
  };

  const currentStore = useSelector((state) => state.current_store);
  const storeId = currentStore?.id;
  const customerId = location.state?.customer_id || null;
  const productId = location.state?.product_id || null;

  const rawPermissions = useSelector((state) => state.userpermission_data);
  const permissions = useMemo(() => rawPermissions || [], [rawPermissions]);
  const hasPermission = React.useCallback(
    (perm) => permissions.includes(perm),
    [permissions],
  );

  // Query warranty claims
  const { data: claimsData, isLoading } = useQuery({
    queryKey: [
      "warrantyClaims",
      pagination.pageIndex + 1,
      pagination.pageSize,
      debouncedSearchText,
      statusFilter,
      replacementStatusFilter,
      returnConditionFilter,
      rejectionReasonFilter,
      overdueFilter,
      sortFilter,
      storeId,
      customerId,
      productId,
    ],
    queryFn: async () => {
      const [sortByField, sortDirection] = (sortFilter || "created_at:desc").split(":");

      const params = {
        page: pagination.pageIndex + 1,
        per_page: Math.min(100, Math.max(1, pagination.pageSize)),
        with: ["customer", "product", "store", "handledBy", "documents"],
        sort_by: sortByField || "created_at",
        sort_direction: sortDirection || "desc",
      };

      if (debouncedSearchText) params.search = debouncedSearchText.slice(0, 255);
      if (statusFilter && statusFilter !== "all") params.status = statusFilter;
      if (replacementStatusFilter && replacementStatusFilter !== "all") {
        params.replacement_status = replacementStatusFilter;
      }
      if (returnConditionFilter && returnConditionFilter !== "all") {
        params.return_condition = returnConditionFilter;
      }
      if (
        statusFilter === "rejected" &&
        rejectionReasonFilter &&
        rejectionReasonFilter !== "all"
      ) {
        params.rejection_reason = rejectionReasonFilter;
      }
      if (overdueFilter === "overdue") params.overdue = true;
      if (storeId) params.store_id = storeId;
      if (customerId) params.customer_id = customerId;
      if (productId) params.product_id = productId;

      const res = await warrantyApi.list(params);
      return res.data;
    },
    enabled: hasPermission("warranty claim read"),
  });

  const claims = claimsData?.data || [];
  const meta = claimsData?.meta || {};
  const pageCount = meta.last_page || 1;

  // Mutations for Close and Escape Delete
  const closeMutation = useMutation({
    mutationFn: (id) => warrantyApi.close(id),
    onSuccess: () => {
      toast.success("Claim closed successfully");
      queryClient.invalidateQueries({ queryKey: ["warrantyClaims"] });
    },
    onError: (err) => {
      toast.error(err.response?.data?.message || "Failed to close claim");
      queryClient.invalidateQueries({ queryKey: ["warrantyClaims"] });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id) => warrantyApi.rejectViaDelete(id),
    onSuccess: () => {
      toast.success("Claim rejected / deleted successfully");
      queryClient.invalidateQueries({ queryKey: ["warrantyClaims"] });
    },
    onError: (err) => {
      toast.error(err.response?.data?.message || "Failed to reject claim");
      queryClient.invalidateQueries({ queryKey: ["warrantyClaims"] });
    },
  });

  // Table Columns
  const columns = useMemo(
    () => [
      {
        accessorKey: "claim_number",
        header: "Claim #",
        cell: ({ row }) => {
          const claim = row.original;
          return (
            <div className="flex flex-col">
              <span className="font-semibold text-primary font-mono text-sm hover:underline cursor-pointer">
                #{claim.claim_number || claim.id}
              </span>
              {claim.is_warranty_valid === false && (
                <div className="flex items-center gap-1 mt-0.5 text-[10px] text-amber-600 font-medium">
                  <ShieldAlert className="h-3 w-3 shrink-0" />
                  <span>Warranty Expired</span>
                </div>
              )}
            </div>
          );
        },
      },
      {
        id: "customer",
        header: "Customer",
        cell: ({ row }) => {
          const customer = row.original.customer;
          const customerName =
            customer?.customer_name ||
            customer?.full_name ||
            customer?.name ||
            "Walk-in Customer";
          return (
            <div className="flex flex-col">
              <span className="font-medium text-sm text-foreground">
                {customerName}
              </span>
              {customer?.phone && (
                <span className="text-xs text-muted-foreground">
                  {customer.phone}
                </span>
              )}
            </div>
          );
        },
      },
      {
        id: "product",
        header: "Item & Defect",
        cell: ({ row }) => {
          const claim = row.original;
          const itemDesc =
            claim.product?.name || claim.item_description || "Product";
          const defectDesc = claim.defect_description;
          const serial = claim.serial_number;

          return (
            <div className="max-w-[280px]">
              <div className="font-medium text-sm text-foreground truncate" title={itemDesc}>
                {itemDesc}
              </div>
              {serial && (
                <div className="text-xs font-mono text-muted-foreground mt-0.5">
                  SN: {serial}
                </div>
              )}
              {defectDesc && (
                <div
                  className="text-xs text-muted-foreground mt-0.5 line-clamp-1 italic"
                  title={defectDesc}
                >
                  {defectDesc}
                </div>
              )}
            </div>
          );
        },
      },
      {
        accessorKey: "status",
        header: "Status",
        cell: ({ row }) => {
          const claim = row.original;
          const statusVal = getClaimStatusValue(claim);
          const statusLabel = getClaimStatusLabel(claim);

          const replacementStatusVal =
            typeof claim.replacement_status === "object" && claim.replacement_status !== null
              ? claim.replacement_status.value
              : claim.replacement_status;

          return (
            <div className="flex flex-col gap-1 items-start">
              <Badge variant={statusVariants[statusVal] || "outline"}>
                {statusLabel.toUpperCase()}
              </Badge>

              {/* Assessment Overdue badge on pending claims */}
              {claim.is_assessment_overdue && statusVal === "pending" && (
                <Badge
                  variant="destructive"
                  className="text-[10px] h-4 gap-1 px-1.5"
                  title="Initial inspection overdue per SLA"
                >
                  <Clock className="h-2.5 w-2.5" /> Overdue
                </Badge>
              )}

              {/* Awaiting stock chip */}
              {replacementStatusVal === "pending" && (
                <Badge
                  variant="warning"
                  className="text-[10px] h-4 text-amber-700 bg-amber-50 border-amber-200"
                >
                  Awaiting Stock
                </Badge>
              )}

              {/* Amount due chip on price difference */}
              {claim.price_difference && parseFloat(claim.price_difference) > 0 && (
                <Badge
                  variant="outline"
                  className="text-[10px] h-4 text-amber-700 border-amber-300 font-mono"
                >
                  +${parseFloat(claim.price_difference).toFixed(2)} Due
                </Badge>
              )}
            </div>
          );
        },
      },
      {
        accessorKey: "created_at",
        header: "Date",
        cell: ({ row }) => {
          const dateStr = row.getValue("created_at");
          return (
            <div className="text-xs text-muted-foreground">
              {formatDate(dateStr)}
            </div>
          );
        },
      },
      {
        id: "actions",
        header: "",
        cell: ({ row }) => {
          const claim = row.original;
          const statusVal = getClaimStatusValue(claim);

          const replacementStatusVal =
            typeof claim.replacement_status === "object" && claim.replacement_status !== null
              ? claim.replacement_status.value
              : claim.replacement_status;

          const isAwaitingStock = replacementStatusVal === "pending";
          const isTerminal = ["closed", "rejected"].includes(statusVal);

          // Closed items have no workflow actions; table row click already navigates to view details
          if (statusVal === "closed") {
            return null;
          }

          // Strictly drive actions from allowed_transitions + permissions
          const canAssess =
            hasPermission("warranty claim assess") &&
            canTransition(claim, "under_assessment", "assess");

          const canApprove =
            hasPermission("warranty claim approve") &&
            canTransition(claim, "approved", "approve");

          const canFulfil =
            hasPermission("warranty claim fulfil") &&
            (isAwaitingStock || canTransition(claim, "fulfil", "fulfilled"));

          const canResolve =
            (hasPermission("warranty claim update_status") ||
              hasPermission("warranty claim approve")) &&
            canTransition(claim, "resolved", "resolve");

          const canClose =
            (hasPermission("warranty claim update_status") ||
              hasPermission("warranty claim close")) &&
            canTransition(claim, "closed", "close");

          const canReject =
            hasPermission("warranty claim reject") &&
            canTransition(claim, "rejected", "reject");

          const canSupplierClaim =
            hasPermission("warranty claim supplier_claim") && !isTerminal;

          const canDelete =
            (hasPermission("warranty claim delete") || hasPermission("warranty claim reject")) &&
            statusVal !== "closed";

          const hasActions =
            canAssess ||
            canApprove ||
            canFulfil ||
            canResolve ||
            canClose ||
            canReject ||
            canSupplierClaim ||
            canDelete;

          if (!hasActions) {
            return null;
          }

          return (
            <DropdownMenu>
              <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                <Button variant="ghost" className="h-8 w-8 p-0">
                  <span className="sr-only">Open menu</span>
                  <MoreVertical className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
                <DropdownMenuLabel>Claim Actions</DropdownMenuLabel>

                {canAssess && (
                  <DropdownMenuItem
                    onClick={() => {
                      setActiveClaim(claim);
                      setAssessModalOpen(true);
                    }}
                  >
                    <Clock className="mr-2 h-4 w-4 text-blue-500" /> Assess Item
                  </DropdownMenuItem>
                )}

                {canApprove && (
                  <DropdownMenuItem
                    onClick={() => {
                      setActiveClaim(claim);
                      setApproveModalOpen(true);
                    }}
                  >
                    <CheckCircle className="mr-2 h-4 w-4 text-emerald-500" /> Approve Claim
                  </DropdownMenuItem>
                )}

                {canFulfil && (
                  <DropdownMenuItem
                    onClick={() => {
                      setActiveClaim(claim);
                      setFulfilModalOpen(true);
                    }}
                  >
                    <PackageCheck className="mr-2 h-4 w-4 text-blue-500" /> Fulfil Replacement
                  </DropdownMenuItem>
                )}

                {canResolve && (
                  <DropdownMenuItem
                    onClick={() => {
                      setActiveClaim(claim);
                      setResolveModalOpen(true);
                    }}
                  >
                    <CheckCircle2 className="mr-2 h-4 w-4 text-emerald-500" /> Resolve Claim
                  </DropdownMenuItem>
                )}

                {canClose && (
                  <DropdownMenuItem
                    onClick={() => {
                      setActiveClaim(claim);
                      setCloseConfirmOpen(true);
                    }}
                  >
                    <CheckCircle className="mr-2 h-4 w-4 text-muted-foreground" /> Close Claim
                  </DropdownMenuItem>
                )}

                {canReject && (
                  <DropdownMenuItem
                    className="text-destructive focus:text-destructive"
                    onClick={() => {
                      setActiveClaim(claim);
                      setRejectModalOpen(true);
                    }}
                  >
                    <XCircle className="mr-2 h-4 w-4" /> Reject Claim
                  </DropdownMenuItem>
                )}

                {canSupplierClaim && (
                  <DropdownMenuItem
                    onClick={() => {
                      setActiveClaim(claim);
                      setSupplierModalOpen(true);
                    }}
                  >
                    <Truck className="mr-2 h-4 w-4 text-purple-500" /> Raise Supplier RMA
                  </DropdownMenuItem>
                )}

                {canDelete && (
                  <>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                      className="text-destructive focus:text-destructive"
                      onClick={() => {
                        setActiveClaim(claim);
                        setDeleteConfirmOpen(true);
                      }}
                    >
                      <Trash2 className="mr-2 h-4 w-4" /> Delete / Reject Claim
                    </DropdownMenuItem>
                  </>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          );
        },
      },
    ],
    [hasPermission],
  );

  return (
    <PageContainer>
      <PageHeader>
        <PageHeaderHeading
          title="Warranty Claims"
          subtitle="Manage customer intake, inspection, remedy approvals, and fulfilment"
        />
        <PageHeaderActions>
          {hasPermission("warranty claim create") && (
            <Button onClick={() => navigate(all_routes.addWarrantyClaim)}>
              <PlusCircle className="mr-2 h-4 w-4" />
              New Claim (Intake)
            </Button>
          )}
        </PageHeaderActions>
      </PageHeader>

      {/* Filter Controls */}
      <FilterCard>
        <FilterSearch
          placeholder="Search claim #, customer, product, serial..."
          value={searchText}
          onChange={(e) => handleFilterChange(setSearchText)(e.target.value)}
        />

        <Select
          value={statusFilter}
          onValueChange={handleFilterChange((val) => {
            setStatusFilter(val);
            if (val !== "rejected") setRejectionReasonFilter("all");
          })}
        >
          <SelectTrigger className="w-[170px]">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Statuses</SelectItem>
            <SelectItem value="pending">Pending</SelectItem>
            <SelectItem value="under_assessment">Under Assessment</SelectItem>
            <SelectItem value="approved">Approved</SelectItem>
            <SelectItem value="in_progress">In Progress</SelectItem>
            <SelectItem value="resolved">Resolved</SelectItem>
            <SelectItem value="closed">Closed</SelectItem>
            <SelectItem value="rejected">Rejected</SelectItem>
          </SelectContent>
        </Select>

        <Select
          value={replacementStatusFilter}
          onValueChange={handleFilterChange(setReplacementStatusFilter)}
        >
          <SelectTrigger className="w-[170px]">
            <SelectValue placeholder="Fulfilment Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Fulfilment</SelectItem>
            <SelectItem value="pending">Awaiting Stock</SelectItem>
            <SelectItem value="fulfilled">Fulfilled</SelectItem>
            <SelectItem value="none">No Replacement</SelectItem>
          </SelectContent>
        </Select>

        <Select
          value={returnConditionFilter}
          onValueChange={handleFilterChange(setReturnConditionFilter)}
        >
          <SelectTrigger className="w-[160px]">
            <SelectValue placeholder="Condition" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Conditions</SelectItem>
            <SelectItem value="defective">Defective</SelectItem>
            <SelectItem value="resaleable">Resaleable</SelectItem>
            <SelectItem value="parts_only">Parts Only</SelectItem>
            <SelectItem value="no_return">No Return</SelectItem>
          </SelectContent>
        </Select>

        {statusFilter === "rejected" && (
          <Select
            value={rejectionReasonFilter}
            onValueChange={handleFilterChange(setRejectionReasonFilter)}
          >
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder="Rejection Reason" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Rejection Reasons</SelectItem>
              <SelectItem value="out_of_warranty">Out of Warranty</SelectItem>
              <SelectItem value="accidental_damage">Accidental Damage</SelectItem>
              <SelectItem value="no_proof_of_purchase">No Proof of Purchase</SelectItem>
              <SelectItem value="unauthorised_repair">Unauthorised Repair</SelectItem>
              <SelectItem value="consumable_wear">Consumable Wear</SelectItem>
              <SelectItem value="policy_exclusion">Policy Exclusion</SelectItem>
              <SelectItem value="other">Other</SelectItem>
            </SelectContent>
          </Select>
        )}

        <Select
          value={overdueFilter}
          onValueChange={handleFilterChange(setOverdueFilter)}
        >
          <SelectTrigger className="w-[150px]">
            <SelectValue placeholder="SLA Overdue" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Timelines</SelectItem>
            <SelectItem value="overdue">Overdue Only</SelectItem>
          </SelectContent>
        </Select>

        <Select
          value={sortFilter}
          onValueChange={handleFilterChange(setSortFilter)}
        >
          <SelectTrigger className="w-[170px]">
            <SelectValue placeholder="Sort By" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="created_at:desc">Newest First</SelectItem>
            <SelectItem value="created_at:asc">Oldest First</SelectItem>
            <SelectItem value="claim_number:asc">Claim # (A-Z)</SelectItem>
            <SelectItem value="claim_number:desc">Claim # (Z-A)</SelectItem>
            <SelectItem value="status:asc">Status</SelectItem>
            <SelectItem value="return_condition:asc">Condition</SelectItem>
            <SelectItem value="resolution_due_at:asc">Resolution Due</SelectItem>
          </SelectContent>
        </Select>
      </FilterCard>

      {/* Claims Data Table */}
      <TableCard>
        <DataTable
          columns={columns}
          data={claims}
          pageCount={pageCount}
          pagination={pagination}
          setPagination={setPagination}
          loading={isLoading}
          onRowClick={
            hasPermission("warranty claim read")
              ? (row) => navigate(`/warranty-claims/${row.id}`)
              : undefined
          }
        />
      </TableCard>

      {/* Action Modals */}
      <AssessClaimModal
        open={assessModalOpen}
        onOpenChange={setAssessModalOpen}
        claim={activeClaim}
        onDone={() => queryClient.invalidateQueries({ queryKey: ["warrantyClaims"] })}
      />

      <ApproveClaimModal
        open={approveModalOpen}
        onOpenChange={setApproveModalOpen}
        claim={activeClaim}
        onDone={() => queryClient.invalidateQueries({ queryKey: ["warrantyClaims"] })}
      />

      <FulfilReplacementModal
        open={fulfilModalOpen}
        onOpenChange={setFulfilModalOpen}
        claim={activeClaim}
        onDone={() => queryClient.invalidateQueries({ queryKey: ["warrantyClaims"] })}
      />

      <RejectClaimModal
        open={rejectModalOpen}
        onOpenChange={setRejectModalOpen}
        claim={activeClaim}
        onDone={() => queryClient.invalidateQueries({ queryKey: ["warrantyClaims"] })}
      />

      <ResolveClaimModal
        open={resolveModalOpen}
        onOpenChange={setResolveModalOpen}
        claim={activeClaim}
        onDone={() => queryClient.invalidateQueries({ queryKey: ["warrantyClaims"] })}
      />

      <SupplierRmaModal
        open={supplierModalOpen}
        onOpenChange={setSupplierModalOpen}
        claim={activeClaim}
        onDone={() => queryClient.invalidateQueries({ queryKey: ["warrantyClaims"] })}
      />

      {/* Close Claim Confirm Dialog */}
      <AlertDialog open={closeConfirmOpen} onOpenChange={setCloseConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Close this claim?</AlertDialogTitle>
            <AlertDialogDescription>
              Claim #{activeClaim?.claim_number} will be marked as closed. This action is terminal and finalized.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (activeClaim?.id) {
                  closeMutation.mutate(activeClaim.id);
                }
              }}
              disabled={closeMutation.isPending}
            >
              {closeMutation.isPending ? "Closing..." : "Close Claim"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Escape Hatch Delete Confirm Dialog */}
      <AlertDialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Reject and delete claim?</AlertDialogTitle>
            <AlertDialogDescription>
              This will reject claim #{activeClaim?.claim_number} via the escape hatch.
              The associated serial number will be returned to sold status.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={() => {
                if (activeClaim?.id) {
                  deleteMutation.mutate(activeClaim.id);
                }
              }}
              disabled={deleteMutation.isPending}
            >
              {deleteMutation.isPending ? "Processing..." : "Confirm Reject / Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </PageContainer>
  );
};

export default WarrantyClaimsList;
